import type { ReactNode } from 'react';
import { Children, useEffect, useId, useState } from 'react';

import classNames from 'classnames';

import styles from './MultipleItemsSlideshow.module.css';

import { useHtmlElement } from '../../hooks';
import { useSlideshow } from '../../hooks/useSlideshow';
import { ButtonBase } from '../ButtonBase';
import { IndexButton } from '../IndexButton';
import type { IndexButtonVariant } from '../IndexButton';
import { ScrollSnap } from '../ScrollSnap';
import { isVisible } from '../Slideshow/utils/isVisible';
import { Arrow } from './Arrow';
import { Slide } from './Slide';

export interface MultipleItemsSlideshowProps {
  /** The label used for this slide show. Required for accessibility. */
  label: string;
  /** Toggle display of index dots indicators */
  withDots?: boolean;
  /** Toggle next/previous buttons */
  withButtons?: boolean;
  /** Class name for the "next" button */
  nextButtonClass?: string;
  /** Class name for the "previous" button */
  prevButtonClass?: string;
  /** Callback when the current slide changes via touch gestures */
  onSlideChange?: (nextIndex: number) => void;
  /** Controlled active index */
  activeIndex?: number;
  /** Scroll behavior when using programmatic scroll */
  scrollBehavior?: ScrollBehavior;
  /** How many slides to show at once */
  slidesToShow?: number;
  /** The slides themselves */
  children: ReactNode;
  /** Optional dots variant */
  dotsVariant?: IndexButtonVariant;
  className?: string;
}

const GAP_PX = 12;
const STEP = 1;

export function MultipleItemsSlideshow({
  children,
  label,
  withDots = false,
  withButtons = false,
  nextButtonClass,
  prevButtonClass,
  onSlideChange,
  dotsVariant = 'kale',
  scrollBehavior: scrollBehaviorProp = 'auto',
  activeIndex: activeIndexProp,
  slidesToShow = 1,
  className,
}: MultipleItemsSlideshowProps) {
  const id = useId();
  const count = Children.count(children);
  const [containerEl, containerRef] = useHtmlElement();
  const [autoTransition, setAutoTransition] = useState(false);
  const [smoothFirstToLast, setSmoothFirstToLast] = useState(false);
  const [smoothLastToFirst, setSmoothLastToFirst] = useState(false);

  const { activeIndex, goToSlide } = useSlideshow({
    scrollableEl: containerEl,
    scrollBehavior: autoTransition ? 'auto' : scrollBehaviorProp,
    getIsActiveSlide: isVisible,
    onSlideChange,
    activeIndex: activeIndexProp,
  });

  const totalGap = GAP_PX * (slidesToShow - 1);
  const initialWidthCalc = `calc((100% - ${totalGap}px) / ${slidesToShow})`;
  const initialMaxIndex = Math.max(count - slidesToShow, 0);

  const [widthCalc, setWidthCalc] = useState<string>(initialWidthCalc);
  const [maxIndex, setMaxIndex] = useState(initialMaxIndex);

  useEffect(() => {
    setWidthCalc(`calc((100% - ${totalGap}px) / ${slidesToShow})`);
    setMaxIndex(Math.max(count - slidesToShow, 0));
  }, [slidesToShow, count, totalGap]);

  useEffect(() => {
    const handleResize = () => {
      setWidthCalc(`calc((100% - ${totalGap}px) / ${slidesToShow})`);
      setMaxIndex(Math.max(count - slidesToShow, 0));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [slidesToShow, count, totalGap]);

  const isFirst = activeIndex === 0;
  const isLast = activeIndex >= maxIndex;

  const disablePrev = isFirst;
  const disableNext = isLast;

  const handlePrev = (): void => {
    if (disablePrev) {
      return;
    }
    goToSlide(Math.max(activeIndex - STEP, 0));
    return;
  };

  const handleNext = (): void => {
    if (disableNext) {
      return;
    }
    goToSlide(Math.min(activeIndex + STEP, maxIndex));
    return;
  };

  useEffect(() => {
    if (smoothFirstToLast && activeIndex === 1) {
      goToSlide(0);
    }
    if (autoTransition && isFirst) {
      goToSlide(count);
    }
    if (autoTransition && smoothFirstToLast && activeIndex === count) {
      setSmoothFirstToLast(false);
      setAutoTransition(false);
    }
  }, [
    smoothFirstToLast,
    isFirst,
    activeIndex,
    goToSlide,
    count,
    autoTransition,
  ]);

  useEffect(() => {
    if (smoothLastToFirst && isLast) {
      goToSlide(count);
    }
    if (autoTransition && smoothLastToFirst && activeIndex === count) {
      goToSlide(0);
    }
    if (autoTransition && smoothLastToFirst && isFirst) {
      setSmoothLastToFirst(false);
      setAutoTransition(false);
    }
  }, [
    smoothLastToFirst,
    isFirst,
    isLast,
    activeIndex,
    goToSlide,
    count,
    autoTransition,
  ]);

  return (
    <section
      className={classNames(className)}
      aria-label={label}
      style={{
        position: 'relative',
      }}
    >
      <div className={styles.container}>
        <ScrollSnap
          as="ul"
          id={id}
          containerRef={containerRef}
          className={styles.horizontalSnap}
          style={{ gap: `${GAP_PX}px` }}
        >
          {Children.map(children, (child, i) => (
            <Slide
              key={i}
              aria-label={`${i + 1} of ${count}`}
              style={{
                flex: `0 0 ${widthCalc}`,
                width: widthCalc,
              }}
            >
              {child}
            </Slide>
          ))}
        </ScrollSnap>

        {withButtons && (
          <>
            <ButtonBase
              aria-controls={id}
              aria-label="previous"
              className={classNames(
                styles.previous,
                prevButtonClass,
                styles.outside,
                { [styles.disabledArrow]: disablePrev }
              )}
              onClick={handlePrev}
              disabled={disablePrev}
            >
              <Arrow />
            </ButtonBase>
            <ButtonBase
              aria-controls={id}
              aria-label="next"
              className={classNames(
                styles.next,
                nextButtonClass,
                styles.invertedArrow,
                styles.outside,
                { [styles.disabledArrow]: disableNext }
              )}
              onClick={handleNext}
              disabled={disableNext}
            >
              <Arrow />
            </ButtonBase>
          </>
        )}
      </div>

      {withDots && (
        <div className={classNames(styles.dots, styles.outside)}>
          {Array.from({ length: maxIndex + 1 }, (_, position) => (
            <IndexButton
              key={position}
              aria-controls={id}
              aria-label={`Go to slide position ${position}`}
              active={
                position === activeIndex &&
                !smoothFirstToLast &&
                !smoothLastToFirst
              }
              onClick={() => goToSlide(position)}
              variant={dotsVariant}
            />
          ))}
        </div>
      )}
    </section>
  );
}
